import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/api/client";
import { Button } from "@/components/ui/button";
import { CalendarClock, Clock, MapPin, Phone, Download, Navigation, CheckCircle2 } from "lucide-react";
import { openInMaps, openDirections } from "@/lib/jobActions";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import FloatingActions from "@/components/landing/FloatingActions";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtHours = (n) => `${Number(n || 0).toFixed(2)} h`;

const fmtDate = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

const arrivalWindow = (start) => {
  if (!start) return "";
  const [h, m] = start.split(":").map(Number);
  const base = new Date(2000, 0, 1, h || 0, m || 0);
  const end = new Date(base.getTime() + 60 * 60 * 1000);
  const fmt = (dt) => dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${fmt(base)} – ${fmt(end)}`;
};

export default function PublicBooking() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/booking/${id}`)
      .then((r) => setJob(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "Booking not found" : "Unable to load booking"));
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar />
        <div className="max-w-xl mx-auto px-6 py-32 text-center">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>{err}</h1>
          <p className="text-muted-foreground">Please contact us at (331) 645-9072 if you believe this is an error.</p>
        </div>
        <Footer />
      </div>
    );
  }
  if (!job) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-muted-foreground">Loading booking...</div>
      </div>
    );
  }

  const rate = Number(job.hourly_rate || 0);
  const hrs = Number(job.estimated_hours || 0);
  const labor = rate * hrs;
  const extras = job.extra_charges || [];
  const extrasTotal = extras.reduce((s, e) => s + Number(e.amount || 0), 0);
  const subtotal = labor + extrasTotal || Number(job.total_payment || 0);
  const deposit = Number(job.deposit || 0);
  const balance = subtotal - deposit;
  const bookingNo = (job.id || "").slice(0, 8).toUpperCase();

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-booking-page">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Confirmation banner */}
          <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-6 md:p-8 mb-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-400 flex-shrink-0">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-primary font-bold">Booking Confirmation</div>
                <h1 className="mt-1 text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
                  Hi {job.client_name?.split(" ")[0] || "there"}, you're booked!
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">Confirmation <span className="font-mono text-foreground">#{bookingNo}</span></p>
              </div>
            </div>
          </div>

          {/* Schedule card */}
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5 mb-2">
                <CalendarClock className="h-3 w-3" /> Date
              </div>
              <div className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{fmtDate(job.date)}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5 mb-2">
                <Clock className="h-3 w-3" /> Arrival window
              </div>
              <div className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{arrivalWindow(job.start_time)}</div>
              <div className="text-xs text-muted-foreground mt-1">Our crew will arrive within this open window.</div>
            </div>
          </div>

          {/* Service + Addresses */}
          <div className="rounded-xl border border-border bg-card p-5 md:p-6 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-primary font-bold mb-3">Service</div>
            <div className="text-lg font-semibold mb-4">{job.service_type}</div>

            <div className="space-y-3">
              <button
                onClick={() => openInMaps(job.address_from)}
                disabled={!job.address_from}
                className="w-full text-left flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary transition-colors disabled:opacity-60 disabled:hover:border-border"
                data-testid="pub-from-map"
              >
                <div className="h-8 w-8 rounded-lg bg-primary/10 border border-primary/25 flex items-center justify-center text-primary flex-shrink-0">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Move from</div>
                  <div className="font-semibold break-words">{job.address_from || "—"}</div>
                </div>
              </button>
              <button
                onClick={() => openInMaps(job.address_to)}
                disabled={!job.address_to}
                className="w-full text-left flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary transition-colors disabled:opacity-60 disabled:hover:border-border"
                data-testid="pub-to-map"
              >
                <div className="h-8 w-8 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-400 flex-shrink-0">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Move to</div>
                  <div className="font-semibold break-words">{job.address_to || "—"}</div>
                </div>
              </button>
              {job.address_from && job.address_to && (
                <Button
                  onClick={() => openDirections(job.address_from, job.address_to)}
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="pub-directions-btn"
                >
                  <Navigation className="h-4 w-4 mr-1.5" /> Get directions in Google Maps
                </Button>
              )}
            </div>
          </div>

          {/* Estimated charges */}
          <div className="rounded-xl border border-border bg-card p-5 md:p-6 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-primary font-bold mb-4">Estimated Charges</div>
            <div className="divide-y divide-border">
              {rate > 0 && hrs > 0 && (
                <div className="py-3 flex items-baseline justify-between gap-4">
                  <div>
                    <div className="font-semibold">Labor</div>
                    <div className="text-xs text-muted-foreground">{fmtHours(hrs)} × {fmtMoney(rate)}/hr</div>
                  </div>
                  <div className="font-semibold">{fmtMoney(labor)}</div>
                </div>
              )}
              {extras.map((x, i) => (
                <div key={i} className="py-3 flex items-baseline justify-between gap-4">
                  <div>
                    <div className="font-semibold">{x.label || "Extra charge"}</div>
                    <div className="text-xs text-muted-foreground">Extra charge</div>
                  </div>
                  <div className="font-semibold">{fmtMoney(x.amount)}</div>
                </div>
              ))}
              {rate === 0 && hrs === 0 && extras.length === 0 && (
                <div className="py-3 flex items-baseline justify-between gap-4">
                  <div className="font-semibold">Total quoted</div>
                  <div className="font-semibold">{fmtMoney(job.total_payment)}</div>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-border space-y-1.5">
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">Estimated subtotal</span>
                <span className="font-semibold">{fmtMoney(subtotal)}</span>
              </div>
              {deposit > 0 && (
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-muted-foreground">Deposit received</span>
                  <span className="font-semibold text-emerald-400">− {fmtMoney(deposit)}</span>
                </div>
              )}
              <div className="mt-3 rounded-lg bg-primary/10 border border-primary/30 px-4 py-3 flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-widest text-primary font-bold">Balance due on move day</span>
                <span className="text-2xl font-bold text-primary" style={{ fontFamily: "Outfit" }}>{fmtMoney(balance)}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="grid sm:grid-cols-2 gap-3 mb-8">
            <Button
              onClick={() => window.open(`${API_BASE}/booking/${job.id}/pdf`, "_blank", "noopener,noreferrer")}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              data-testid="pub-pdf-btn"
            >
              <Download className="h-4 w-4 mr-1.5" /> Download PDF
            </Button>
            <a
              href="tel:+13316459072"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border hover:border-primary hover:text-primary px-6 py-2.5 text-sm font-semibold transition-colors"
              data-testid="pub-call-btn"
            >
              <Phone className="h-4 w-4" /> Call (331) 645-9072
            </a>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            This is a booking confirmation, not a final invoice. Final charges are based on actual hours worked and any additional services requested on move day.
          </p>
        </div>
      </main>
      <Footer />
      <FloatingActions />
    </div>
  );
}
